import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./Projectstepper.css";

import { getJson } from "./api";

import StepBom from "./steps/StepBom";
import StepBomReview from "./steps/stepBomReview";
import StepPcb from "./steps/StepPcb";
import StepGerber from "./steps/StepGerber";
import StepQuote from "./steps/stepquote";

export default function ProjectStepper() {
  const { projectId } = useParams();

  const [pcbMode, setPcbMode] = useState(""); // "", "provide", "order"
  const [initLoading, setInitLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [pcbQty, setPcbQty] = useState(1);

  const [completed, setCompleted] = useState({
    bom: false,
    bom_review: false,
    pcb: false,
    gerber: false,
    quote: false,
  });

  const [uploaded, setUploaded] = useState({ bomName: "", gerberName: "" });
  const [existingQuote, setExistingQuote] = useState(null);
  const [initialPcbPayload, setInitialPcbPayload] = useState(null);
  const steps = useMemo(() => {
    const base = [
      { key: "bom", label: "BOM" },
      { key: "bom_review", label: "Revue BOM" },
      { key: "pcb", label: "PCB" },
    ];
    if (pcbMode !== "provide") {
      base.push({ key: "gerber", label: "Gerber" });
    }

    base.push({ key: "quote", label: "Devis" });
    return base;
  }, [pcbMode]);
  useEffect(() => {
    if (activeIndex >= steps.length) {
      setActiveIndex(Math.max(0, steps.length - 1));
    }
  }, [steps, activeIndex]);
  useEffect(() => {
    let mounted = true;

    (async () => {
      setInitLoading(true);
      try {
        const raw = localStorage.getItem(`pcb_step_state_v1:${projectId}`);
        if (raw) {
          const st = JSON.parse(raw);
          if (st?.mode) setPcbMode(st.mode);
        }
      } catch (_) {}

      try {
        const rd = await getJson(`/user/projects/${projectId}/readiness/`);
        if (!mounted) return;

        const checks = rd?.checks || {};

        const pcbDone =
          (!!checks.pcb_spec_ok && !!checks.assembly_spec_ok) ||
          !!checks.pcb_provided_ok ||
          !!checks.pcb_provided;

        const nextCompleted = {
          bom: !!checks.bom_parsed,
          bom_review: false, // on force review
          pcb: pcbDone,
          gerber: !!checks.gerber_ok && String(checks.gerber_status) !== "error",
          quote: false,
        };
        try {
          const cfg = await getJson(`/user/projects/${projectId}/pcb-config/`);
          if (mounted && cfg?.pcb_spec) {
            setInitialPcbPayload({
              pcb_spec: {
                layers: cfg.pcb_spec.layers ?? "",
                size_x: cfg.pcb_spec.size_x ?? "",
                size_y: cfg.pcb_spec.size_y ?? "",
                material: cfg.pcb_spec.material ?? "",
                thickness: cfg.pcb_spec.thickness ?? "",
                external_copper_thickness: cfg.pcb_spec.external_copper_thickness ?? "",
                inner_copper_thickness: cfg.pcb_spec.inner_copper_thickness ?? "",
                finish: cfg.pcb_spec.finish ?? "",
                stackup_type: cfg.pcb_spec.stackup_type ?? "",
                solder_color_top: cfg.pcb_spec.solder_color_top ?? "",
                solder_color_bot: cfg.pcb_spec.solder_color_bot ?? "",
                silkscreen_ink_color: cfg.pcb_spec.silkscreen_ink_color ?? "",
                impedance_control: !!cfg.pcb_spec.impedance_control,
              },
              assembly_spec: {
                smd_side: cfg.assembly_spec?.smd_side ?? "",
                press_fit: !!cfg.assembly_spec?.press_fit,
                via_fill: !!cfg.assembly_spec?.via_fill,
                min_drill_size: cfg.assembly_spec?.min_drill_size ?? "",
              },
            });
            if (cfg?.pcb_spec?.client_pcb_mode === "provide" || cfg?.pcb_spec?.client_pcb_mode === "order") {
              setPcbMode(cfg.pcb_spec.client_pcb_mode);
            }
          }
        } catch (_) {}
        try {
          const proj = await getJson(`/user/projects/${projectId}/`);
          if (mounted && proj?.quantity && Number(proj.quantity) > 0) {
            setPcbQty(Number(proj.quantity));
          }
        } catch (_) {}
        try {
          const qres = await getJson(`/user/projects/${projectId}/quote/`);
          if (!mounted) return;

          if (qres?.exists && qres?.quote) {
            setExistingQuote(qres.quote);
            nextCompleted.quote = true;
            const quoteIdx = steps.findIndex((s) => s.key === "quote");
            setActiveIndex(quoteIdx >= 0 ? quoteIdx : 0);
          } else {
            const firstMissing = !nextCompleted.bom ? 0 : 1;
            setActiveIndex(firstMissing);
          }
        } catch (_) {
          const firstMissing = !nextCompleted.bom ? 0 : 1;
          setActiveIndex(firstMissing);
        }

        setCompleted(nextCompleted);
      } finally {
        if (mounted) setInitLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [projectId]);

  const activeStep = steps[activeIndex] || steps[0];

  const completedCount = steps.reduce((acc, s) => acc + (completed[s.key] ? 1 : 0), 0);
  const progressPct = Math.round((completedCount / steps.length) * 100);

  const canGoTo = (index) => {
    for (let i = 0; i < index; i++) {
      if (!completed[steps[i].key]) return false;
    }
    return true;
  };

  const goNext = () => activeIndex < steps.length - 1 && setActiveIndex((i) => i + 1);
  const goPrev = () => activeIndex > 0 && setActiveIndex((i) => i - 1);

  const markDoneAndNext = (key) => {
    setCompleted((p) => ({ ...p, [key]: true }));
    goNext();
  };

  if (initLoading) {
    return (
      <div className="stepper-page">
        <div className="stepper-card">
          <div className="projects-info">Chargement du projet…</div>
        </div>
      </div>
    );
  }

  if (!activeStep) return null;

  return (
    <div className="stepper-page">
      <div className="stepper-card">
        <div className="stepper-header">
          {/* <div>
            <p className="stepper-subtitle">Wizard : BOM → Review → PCB → Gerber → Quote</p>
          </div> */}
          <div className="progress-badge">{progressPct}%</div>
        </div>

        <div className="progress-wrap">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="progress-label">
            Progression : <b>{completedCount}</b>/{steps.length} étapes
          </div>
        </div>

        <div className="stepper-bar">
          {steps.map((s, idx) => {
            const isActive = idx === activeIndex;
            const isDone = completed[s.key];
            const isDisabled = !canGoTo(idx);

            return (
              <button
                key={s.key}
                type="button"
                className={["stepper-step", isActive ? "active" : "", isDone ? "done" : "", isDisabled ? "disabled" : ""].join(" ")}
                onClick={() => !isDisabled && setActiveIndex(idx)}
              >
                <span className="stepper-dot">{isDone ? "OK" : idx + 1}</span>
                <span className="stepper-label">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="stepper-content">
          {activeStep.key === "bom" && (
            <StepBom
              projectId={projectId}
              uploadedName={uploaded.bomName}
              onSuccess={(data, filename) => {
                setUploaded((p) => ({ ...p, bomName: filename || p.bomName }));
                if (data?.pcbQty) setPcbQty(data.pcbQty);
                markDoneAndNext("bom");
              }}
            />
          )}

          {activeStep.key === "bom_review" && (
            <StepBomReview
              projectId={projectId}
              onSuccess={() => {
                markDoneAndNext("bom_review");
              }}
            />
          )}

          {activeStep.key === "pcb" && (
  <StepPcb
    projectId={projectId}
    onPrev={() => {}}
    onNext={() => {
      markDoneAndNext("pcb");
    }}
    onModeChange={(m) => setPcbMode(m)}
    initialPayload={initialPcbPayload}
    projectQty={pcbQty}
    onSuccess={(saved, meta) => {
      if (meta?.mode === "factory" && saved) setInitialPcbPayload(saved);
      markDoneAndNext("pcb");
    }}
  />
)}

          {activeStep.key === "gerber" && (
            <StepGerber
              projectId={projectId}
              isRequired={pcbMode !== "provide"}
              uploadedName={uploaded.gerberName}
              onSuccess={(data, filename) => {
                setUploaded((p) => ({ ...p, gerberName: filename || p.gerberName }));
                markDoneAndNext("gerber");
              }}
            />
          )}

          {activeStep.key === "quote" && (
            <StepQuote
              projectId={projectId}
              initialQuote={existingQuote}
              initialPcbQty={pcbQty}
              onFixBom={() => {
                const idx = steps.findIndex((s) => s.key === "bom_review");
                setActiveIndex(idx >= 0 ? idx : 1);
                setCompleted((p) => ({ ...p, quote: false }));
              }}
            />
          )}
        </div>

        <div className="stepper-footer">
          <button className="btn" onClick={goPrev} disabled={activeIndex === 0}>
            Précédent
          </button>

          <div className="stepper-status">
            Étape {activeIndex + 1}/{steps.length} :{" "}
            <span className="muted">{completed[activeStep.key] ? "Complétée" : "En cours"}</span>
          </div>

          <button
            className="btn"
            onClick={goNext}
            disabled={activeIndex === steps.length - 1 || !completed[activeStep.key]}
            title={!completed[activeStep.key] ? "Terminez cette étape pour continuer" : ""}
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
}
